import classNames from 'classnames';
import Image from 'next/image';
import { Button } from '../../../components/ui/button/Button';
import {
  Shimmer,
  toBase64,
} from '../../../components/utility/imageShimmer/ImageShimmer';
import type { ImageType } from '../../../types.d/storeTypes';
import styles from './PLPPromo.module.scss';

export type PLPPromoProps = {
  title: string;
  subtitle: string;
  description?: string;
  image: ImageType;
  cta?: {
    title: string;
    url: string;
    newTab: boolean;
  };
  variant?: string;
  textBackgroundColor?: string;
  textColor?: string;
  contentPlacement?: 'bottom' | 'top';
  isLoading: boolean;
};

/**
 * The primary Nail Art Card component.
 * @param props - The card props.
 */
export const PLPPromo = ({
  title,
  subtitle,
  description,
  image,
  cta,
  variant,
  textBackgroundColor,
  textColor,
  contentPlacement,
  isLoading,
}: PLPPromoProps) => {
  if (isLoading) {
    return (
      <section className={[styles.plpPromo, 'is-loading'].join(' ')}></section>
    );
  }

  if (image.url) {
    return (
      <section
        className={classNames(styles.plpPromo, {
          [styles.separate]: variant === 'Text below image',
        })}
      >
        <div
          className={classNames({
            [styles.plpPromo__separateImageWrapper]:
              variant === 'Text below image',
          })}
        >
          <Image
            className={styles.plpPromo__image}
            src={image.url}
            alt={image.title}
            title={image.title}
            fill={true}
            placeholder="blur"
            blurDataURL={`data:image/svg+xml;base64,${toBase64(
              Shimmer(image.width, image.height)
            )}`}
          />
        </div>
        <div
          className={classNames(styles.plpPromo__content, {
            [styles.plpPromo__contentTop]:
              contentPlacement === 'top' && description,
            ['double']: !description,
            [styles.single]: description,
            [styles.separate]: variant === 'Text below image',
          })}
          style={
            variant === 'Text below image'
              ? { backgroundColor: textBackgroundColor, color: textColor }
              : undefined
          }
        >
          <h2
            className={[
              styles.plpPromo__title,
              'text-heading-h2-desktop text-heading-h2-mobile',
            ].join(' ')}
          >
            {title}
          </h2>
          <h3
            className={[
              styles.plpPromo__subtitle,
              'text-heading-h3-desktop text-heading-h3-mobile',
            ].join(' ')}
          >
            {subtitle}
          </h3>

          {description && (
            <p
              className={[
                styles.plpPromo__description,
                'text-heading-h7-desktop text-heading-h7-mobile',
              ].join(' ')}
            >
              {description}
            </p>
          )}

          {cta && (
            <Button
              type="button"
              className={classNames(styles.plpPromo__cta, {
                [styles.separate]: variant === 'Text below image',
              })}
              modifiers="mini-mobile"
              href={cta.url}
              target={cta.newTab ? '_blank' : undefined}
            >
              <span className="text-utility-utility-desktop text-utility-utility-small-mobile">
                {cta.title}
              </span>
            </Button>
          )}
        </div>
      </section>
    );
  }

  return <></>;
};
